// Imports
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { TabItemsProps } from "./type";

// Images
import CheveronRight from "@/assets/images/chevron-right.svg";

const Tab = ({
  items,
  visibleTab,
  setVisibleTab,
  tabBtnCss,
  tabWrapperCss,
  tabRightIconBtn,
  tabLeftIconBtn,
  leftShadow,
  rightShadow,
  tabParentCss,
  bottomLineCss,
}: TabItemsProps): React.ReactElement => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const checkScroll = useCallback((): void => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;

    const hasOverflow = scrollWidth > clientWidth;

    setShowLeft(hasOverflow && scrollLeft > 0);
    setShowRight(hasOverflow && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scroll = (direction: "left" | "right"): void => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const scrollAmount = container.clientWidth * 0.6;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    // Defer the initial check to avoid setState in effect warning
    const timeoutId = setTimeout((): void => {
      checkScroll();
    }, 0);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [checkScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver((): void => {
      checkScroll();
    });

    resizeObserver.observe(container);

    window.addEventListener("resize", checkScroll);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll]);

  return (
    <>
      <div
        className={`relative z-10 flex items-center overflow-hidden mb-5 ${tabParentCss}`}
      >
        {showLeft && (
          <div className="tab-button-wrapper">
            <span className={`shadow left-shadow ${leftShadow}`} />
            <button
              onClick={() => scroll("left")}
              className={`tab-left-icon absolute left-0 top-0 z-20 bg-white h-[29px] w-[29px] flex justify-center items-center rounded-full ${tabLeftIconBtn}`}
            >
              <img
                src={CheveronRight}
                alt="left-icon"
                className="rotate-180 arrow-left-icon"
              />
            </button>
          </div>
        )}
        <div className="relative w-full">
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className={`tab-wrapper flex items-center overflow-x-auto pb-5 -mb-2.5 scrollbar-hide scroll-smooth whitespace-nowrap ${tabWrapperCss}`}
          >
            <div
              className={`absolute top-[38px] left-0 w-full border-t border-border ${bottomLineCss}`}
            />
            {items.map((tab, index) => (
              <button
                key={tab.tabName || index}
                className={` ${tab.tabName === visibleTab ? "active-tab" : ""} ${tabBtnCss}`}
                onClick={() => setVisibleTab(tab.tabName)}
              >
                {tab.tabName}
              </button>
            ))}
          </div>
        </div>
        {showRight && (
          <div className="tab-button-wrapper">
            <span className={`shadow right-shadow ${rightShadow}`} />
            <button
              onClick={() => scroll("right")}
              className={`tab-right-icon absolute right-0 top-0 z-20 bg-white h-[29px] w-[29px] flex justify-center items-center rounded-full ${tabRightIconBtn}`}
            >
              <img
                src={CheveronRight}
                alt="right-icon"
                className="arrow-right-icon"
              />
            </button>
          </div>
        )}
      </div>
      {items.map((tab, index) =>
        tab.tabName === visibleTab ? (
          <div key={tab.tabName || index}>{tab.content}</div>
        ) : null,
      )}
    </>
  );
};

export default Tab;
